import { Timestamp } from 'firebase/firestore';
import { addDays, format } from 'date-fns';
import type { VacationApprovalItem } from '@/features/Vacations/models/vacationApproval.types';
import type { VacationRequest, VacationSegment } from '@/features/Vacations/models/vacation.types';
import { APPROVAL_LEAD_DAYS } from '@/features/Vacations/utils/vacationApprovalDeadline';

const DEMO_MANAGER_EMAIL = 'gestor@rarolabs.com.br';

function firstMondayOnOrAfter(base: Date, offsetDays: number): Date {
    const date = addDays(base, offsetDays);
    const toMonday = (8 - date.getDay()) % 7;
    return addDays(date, toMonday);
}

function buildSegment(order: number, start: Date, days: number): VacationSegment {
    return {
        order,
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(addDays(start, days - 1), 'yyyy-MM-dd'),
        days,
        approved: null,
    } as VacationSegment;
}

interface DemoRequestParams {
    id: string;
    name: string;
    status: VacationRequest['status'];
    modality: VacationRequest['modality'];
    segments: VacationSegment[];
    totalDays: number;
    managerApproval: VacationRequest['managerApproval'];
    sellRequest?: VacationRequest['sellRequest'];
    conflictsSnapshot?: VacationRequest['conflictsSnapshot'];
}

function buildDemoRequest({
    id,
    name,
    status,
    modality,
    segments,
    totalDays,
    managerApproval,
    sellRequest,
    conflictsSnapshot,
}: DemoRequestParams): VacationRequest {
    return {
        id,
        collaboratorId: '',
        collaboratorName: name,
        collaboratorEmail: `${name.toLowerCase().split(' ')[0]}@rarolabs.com.br`,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        requestedAt: Timestamp.now(),
        status,
        modality,
        totalDays,
        segments,
        managerEmail: DEMO_MANAGER_EMAIL,
        managerApproval,
        clientEmails: null,
        clientApproval: { status: 'None', by: null, at: null },
        sellRequest: sellRequest ?? { requested: false, days: 0, status: 'None' },
        dpApproval: { status: 'Pending', by: null, at: null },
        dpSegmentDecisions: null,
        conflictsSnapshot: conflictsSnapshot ?? [],
        correction: null,
        rejectionReason: null,
        timeline: [],
    } as unknown as VacationRequest;
}

function buildDemoItem(request: VacationRequest, role: string, squad: string): VacationApprovalItem {
    return { request, collaboratorRole: role, collaboratorSquad: squad, balanceDays: 30 };
}

export function buildApprovalDemoItems(today: Date): VacationApprovalItem[] {
    const anaStart = firstMondayOnOrAfter(today, 30 + APPROVAL_LEAD_DAYS);
    const brunoStart = firstMondayOnOrAfter(today, 8 + APPROVAL_LEAD_DAYS);
    const carlaStart = firstMondayOnOrAfter(today, 1 + APPROVAL_LEAD_DAYS);

    const anaSegment = buildSegment(1, anaStart, 20);
    const brunoFirst = buildSegment(1, brunoStart, 15);
    const brunoSecond = buildSegment(2, addDays(brunoStart, 30), 15);
    const carlaFirst = buildSegment(1, carlaStart, 10);
    const carlaSecond = buildSegment(2, addDays(carlaStart, 20), 20);

    const ana = buildDemoRequest({
        id: 'demo-approval-ana',
        name: 'Ana Exemplo',
        status: 'PendingManager',
        modality: '30',
        segments: [anaSegment],
        totalDays: 20,
        managerApproval: { status: 'Pending', by: null, at: null, note: null },
        sellRequest: { requested: true, days: 10, status: 'Pending' },
        conflictsSnapshot: [
            {
                allocationId: 'demo-allocation',
                clientName: 'Cliente Exemplo',
                projectName: 'Projeto Exemplo',
                startDate: anaSegment.startDate,
                endDate: anaSegment.endDate,
            },
        ],
    });

    const bruno = buildDemoRequest({
        id: 'demo-approval-bruno',
        name: 'Bruno Exemplo',
        status: 'PendingManager',
        modality: '15+15',
        segments: [brunoFirst, brunoSecond],
        totalDays: 30,
        managerApproval: { status: 'Pending', by: null, at: null, note: null },
    });

    const carla = buildDemoRequest({
        id: 'demo-approval-carla',
        name: 'Carla Exemplo',
        status: 'PendingManager',
        modality: '10+20',
        segments: [carlaFirst, carlaSecond],
        totalDays: 30,
        managerApproval: { status: 'Pending', by: null, at: null, note: null },
    });

    return [
        buildDemoItem(ana, 'Desenvolvedora', 'Squad Exemplo'),
        buildDemoItem(bruno, 'Analista', 'Squad Exemplo'),
        buildDemoItem(carla, 'Designer', 'Squad Exemplo'),
    ];
}

export function buildDecidedDemoItems(today: Date): VacationApprovalItem[] {
    const start = firstMondayOnOrAfter(today, 90 + APPROVAL_LEAD_DAYS);
    const approved = buildDemoRequest({
        id: 'demo-decided-approved',
        name: 'Daniela Exemplo',
        status: 'Scheduled',
        modality: '30',
        segments: [buildSegment(1, start, 30)],
        totalDays: 30,
        managerApproval: { status: 'Approved', by: DEMO_MANAGER_EMAIL, at: Timestamp.now(), note: null },
    });
    const rejected = buildDemoRequest({
        id: 'demo-decided-rejected',
        name: 'Eduardo Exemplo',
        status: 'Rejected',
        modality: '30',
        segments: [buildSegment(1, addDays(start, 14), 30)],
        totalDays: 30,
        managerApproval: {
            status: 'Rejected',
            by: DEMO_MANAGER_EMAIL,
            at: Timestamp.now(),
            note: 'Exemplo de motivo da reprovação.',
        },
    });
    return [
        buildDemoItem(approved, 'Analista', 'Squad Exemplo'),
        buildDemoItem(rejected, 'Analista', 'Squad Exemplo'),
    ];
}
